<template>
    <div v-if="!displayModalChoixSite" class="container">
        <div class="title-container">
            <span>
                {{ $t('intervention.non-preventive.title') }}
                <ReglePastille cle="interventionNonPreventive.definition" />
            </span>
        </div>
        <div class="content-container">
            <div class="filter-container">
                <div v-if="!isUniqueSite" class="filter-site-container">
                    {{ $t('intervention.non-preventive.filter') }}
                    <ReglePastille cle="suiviInterventionsNonPreventives.filtreSiteUnique" />
                    <ButtonBlue
                        :disabled="tableData.length === 0 && isLoading"
                        :on-click="selectionnerSite"
                        :text="$t('bouton.choix.site')"
                        class="button-confirm btn"
                    />
                </div>
                <Filter
                    :filter="this.filter"
                    :siteList="this.siteList"
                    :statusList="this.statusList"
                    class="filter"
                    @addFilterStatus="this.addFilterStatus"
                    @clearFilter="this.clearFilter"
                    @removeFilterStatus="this.removeFilterStatus"
                    @search="search"
                    @setInterventionId="this.setInterventionId"
                    @setPredictedDateFrom="this.setPredictedDateFrom"
                    @setPredictedDateTo="this.setPredictedDateTo"
                    @statusToggleSelectAll="this.statusToggleSelectAll"
                />
            </div>
            <div class="dataTable">
                <div>
                    <h2>
                        {{ this.count }} {{ $t('intervention.non-preventive.dataTable.title') }}
                        <ReglePastille cle="suiviInterventionsNonPreventives.portee" />
                        <ReglePastille cle="suiviInterventionsNonPreventives.selectionTout" />
                    </h2>
                    <BorderedIconButton
                        v-if="!isExportDisabled"
                        :imgUrl="iconeXls"
                        :title="$t('list.export')"
                        @click="exportXls"
                    />
                    <ReglePastille cle="suiviInterventionsNonPreventives.exporterActif" />
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" class="datatable-container" @scroll="handleScroll">
                    <DataTable
                        :count="+count"
                        :isLoading="isLoading"
                        :stop-propa-on-pdf="true"
                        :tableData="this.tableData"
                        :tableHeader="this.tableHeader"
                        @downloadPdf="downloadPdf"
                        @select="goToPage"
                        @sort="doSort"
                    />
                </div>
            </div>
            <WaitingModal v-if="isExportXlsLoading" />
        </div>
    </div>
    <ModalChoixSite
        v-else
        :is-multi-site="true"
        :inclure-termines="true"
        :selected-site="null"
        :selected-sites="filter.siteList"
        @cancel="modalChoixSiteCancel"
        @valider="validerSite"
    />
</template>

<script lang="ts">
import DataTable from '@/components/ui-components/DataTable.vue';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { SortType, sortValue } from '@/models/SortType';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import { defineComponent, onMounted, ref } from 'vue';
import Filter from '@/modules/Common/components/Filter.vue';
import { SUIVI_INTERVENTION } from '@/modules/SuiviInterventions/store/mutation-types';
import { SuiviInterventionActions } from '@/modules/SuiviInterventions/store/action-types';
import { Site } from '@/models/Site';
import { TableHeader } from '@/models/TableHeader';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import { EtatsTravaux } from '@/models/InterventionNonPreventive';
import { store } from '@/plugins/store';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import router from '@/router';
import DownloadPdf from '@/common/utils/DownloadPdf';
import ModalChoixSite from '@/modules/Common/components/ModalChoixSite.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import i18n from '@/plugins/i18n';
import { Intervention } from '@/models/Intervention';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    siteList: any[];
    statusList: string[];
    filter: {
        siteList: any[];
        statusList: string[];
        interventionId: string;
        predictedDateFrom: string;
        predictedDateTo: string;
    };
    siteListTotal: any[];
    debounce: number;
    searchSite: string;
    displayModalChoixSite: boolean;
    isUniqueSite: boolean;
}

export default defineComponent({
    name: 'ListInterventionPage',
    components: {
        ButtonBlue,
        Filter,
        DataTable,
        LoaderComponent,
        WaitingModal,
        BorderedIconButton,
        ModalChoixSite,
        ReglePastille,
    },
    data(): Data {
        return {
            siteList: [],
            statusList: [],
            filter: {
                siteList: [],
                statusList: [],
                interventionId: '',
                predictedDateFrom: '',
                predictedDateTo: '',
            },
            siteListTotal: [],
            debounce: 0,
            searchSite: '',
            displayModalChoixSite: false,
            isUniqueSite: false,
        };
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    beforeMount() {
        this.filter.siteList = this.filterSite;
        SiteListService.getSiteList(new SiteSearchDTO(0, '', true, true)).then(res => {
            if (res.data.length === 1) {
                this.isUniqueSite = true;
                this.displayModalChoixSite = false;
            }
            this.siteList = res.data.map(
                ({ id, chantierId, description }: { id: string; chantierId: number; description: string }) => ({
                    id,
                    chantierId,
                    description,
                })
            );
            this.siteListTotal = this.siteList;
        });

        this.statusList = Object.values(EtatsTravaux);
        this.filter.statusList = this.filterStatus.length > 0 ? this.filterStatus : this.statusList;
        store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_FILTER_STATUS}`, this.filter.statusList);

        this.filter.interventionId = this.filterInterventionId;
        this.filter.predictedDateFrom = this.filterPredictedDateFrom;
        this.filter.predictedDateTo = this.filterPredictedDateTo;
        if (!this.displayModalChoixSite) {
            this.getInterventionData();
        }
    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
    },
    methods: {
        getInterventionData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });
            let isAllSite = false;
            if (this.filterSite.length === this.siteListTotal.length) {
                isAllSite = true;
            }
            store.dispatch(`suiviInterventionModuleState/${SuiviInterventionActions.GET_SUIVIS_INTERVENTION}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                etats: this.filterStatus?.length === this.statusList?.length ? [] : this.filterStatus,
                chantierIds: isAllSite ? [] : this.filterSite?.map(x => x.chantierId),
                adresseProjetIds: isAllSite ? [] : this.filterSite?.map(x => x.id),
                id: this.filterInterventionId !== '' ? this.filterInterventionId : undefined,
                dateDebutPeriodeRechercheParDate:
                    this.filterPredictedDateFrom !== '' ? this.filterPredictedDateFrom : undefined,
                dateFinPeriodeRechercheParDate:
                    this.filterPredictedDateTo !== '' ? this.filterPredictedDateTo : undefined,
            });
        },
        exportXls() {
            const isAllSite = this.filterSite.length === this.siteListTotal.length;
            store.dispatch(`suiviInterventionModuleState/${SuiviInterventionActions.EXPORT_XLS_INTERVENTIONS}`, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                etats: this.filterStatus?.length === this.statusList?.length ? [] : this.filterStatus,
                chantierIds: isAllSite ? [] : [...new Set(this.filterSite?.map(x => x.chantierId))],
                adresseProjetIds: isAllSite ? [] : this.filterSite?.map(x => x.id),
                id: this.filterInterventionId !== '' ? this.filterInterventionId : undefined,
                dateDebutPeriodeRechercheParDate:
                    this.filterPredictedDateFrom !== '' ? this.filterPredictedDateFrom : undefined,
                dateFinPeriodeRechercheParDate:
                    this.filterPredictedDateTo !== '' ? this.filterPredictedDateTo : undefined,
            });
        },
        search() {
            store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.RESET_ITEMS}`);
            this.doFilter();
        },
        doFilter() {
            store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_FILTER_SITE}`, this.filter.siteList);
            store.commit(
                `suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_FILTER_STATUS}`,
                this.filter.statusList
            );
            store.commit(
                `suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_FILTER_INTERVENTION_ID}`,
                this.filter.interventionId
            );
            store.commit(
                `suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_FILTER_PREDICTED_DATE_FROM}`,
                this.filter.predictedDateFrom
            );
            store.commit(
                `suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_FILTER_PREDICTED_DATE_TO}`,
                this.filter.predictedDateTo
            );
            this.getInterventionData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_SORT}`, { sortData, sortStatus });
            store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.RESET_ITEMS}`);
            this.getInterventionData();
        },
        setInterventionId(value: string): void {
            this.filter.interventionId = value;
        },
        setPredictedDateFrom(value: string): void {
            this.filter.predictedDateFrom = value;
        },
        setPredictedDateTo(value: string): void {
            this.filter.predictedDateTo = value;
        },
        addFilterStatus(status: string) {
            this.filter.statusList.push(status);
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(statusData => statusData !== status);
        },
        clearFilter() {
            this.filter.siteList = [];
            this.filter.statusList = this.statusList ?? [];
            this.filter.interventionId = '';
            this.filter.predictedDateFrom = '';
            this.filter.predictedDateTo = '';

            this.doFilter();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.getInterventionData(this.page + 1);
            }
        },
        statusToggleSelectAll() {
            this.filter.statusList = this.filter.statusList.length === this.statusList.length ? [] : this.statusList;
        },
        goToPage(item: { id: { pageName: string; value: number } }) {
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        downloadPdf(item: { id: { pageName: string; value: number } }): void {
            DownloadPdf.getInterventionPdf(item.id.value, 'Rapport_intervention-');
        },
        validerSite(sites: Site[]): void {
            this.siteList = sites;
            this.displayModalChoixSite = false;
            store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_FILTER_SITE}`, sites);
            store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.RESET_ITEMS}`);
            this.getInterventionData();
        },
        modalChoixSiteCancel(): void {
            this.siteList = this.siteListTotal;
            this.displayModalChoixSite = false;
            if (this.tableData.length === 0 && !this.isLoading) this.getInterventionData();
        },
        selectionnerSite(): void {
            this.displayModalChoixSite = true;
        },
    },
    computed: {
        tableData() {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionsItems'].map(
                ({
                    id,
                    objet,
                    type,
                    dateDebutPrevue,
                    dateDebutReelle,
                    dateFinReelle,
                    adresseSite,
                    isAlarmante,
                    etat,
                    isRapportPresent,
                }: Intervention) => {
                    etat = statusName(etat);

                    return {
                        id: { pageName: 'interventionDetailView', value: id, label: id },
                        objet,
                        type,
                        dateDebutPrevue: dateDebutPrevue ? new Date(dateDebutPrevue).toLocaleDateString() : '',
                        dateDebutReelle: dateDebutReelle ? new Date(dateDebutReelle).toLocaleDateString() : '',
                        dateFinReelle: dateFinReelle ? new Date(dateFinReelle).toLocaleDateString() : '',
                        adresseSite,
                        isAlarmante: isAlarmante ? i18n.global.t('yes') : i18n.global.t('no'),
                        statut: etat,
                        pdfExist: isRapportPresent,
                    };
                }
            );
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionsSort'];
        },
        tableHeader(): TableHeader[] {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionsTableHeader'];
        },
        page() {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionsPage'];
        },
        numberOfPages() {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionsNbOfPages'];
        },
        scroll() {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionsScroll'];
        },
        isLoading() {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionsIsLoading'];
        },
        filterSite(): any[] {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionFilterSite'];
        },
        filterStatus(): Array<string> {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionFilterStatus'];
        },
        filterInterventionId() {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionFilterInterventionId'];
        },
        filterPredictedDateFrom() {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionFilterPredictedDateFrom'];
        },
        filterPredictedDateTo() {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionFilterPredictedDateTo'];
        },
        getPreventiveOut() {
            return store.getters.getPreventiveOut;
        },
        count() {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionsCount'];
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportXlsLoading(): boolean {
            return store.getters['suiviInterventionModuleState/getSuiviInterventionsExportXlsIsLoading'];
        },
        isExportDisabled(): boolean {
            return (
                this.filter.siteList?.length === 0 ||
                this.filter.statusList?.length === 0 ||
                this.tableData.length === 0
            );
        },
    },
    watch: {
        tableData() {
            this.filter.statusList = this.filterStatus.length > 0 ? this.filterStatus : this.statusList;
            this.filter.siteList = this.filterSite?.length > 0 ? this.filterSite : this.siteList;
        },
        filterStatus() {
            this.content?.scrollTo({ top: 0 });
        },
        filterSite() {
            this.content?.scrollTo({ top: 0 });
        },
    },
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';

@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 340px);
    }
}

@media screen and (max-width: 1531px) {
    .datatable-container {
        max-height: calc(100vh - 460px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 518px);
    }
}

:deep(.containerBtn) {
    height: 21px;
}
:deep(.icon) {
    height: 20px;
}
.filter-container {
    display: flex;
    padding: 8px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
}
.filter-site-container {
    flex-direction: column;
    width: 15%;
}
.filter {
    width: 100%;
}
:deep(.filter) {
    margin-bottom: 0;
    padding: 0 16px;
}
.button-confirm {
    right: 16px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 18px;
}
</style>
